import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Home, FileText } from 'lucide-react';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('verifying');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`/stripe/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.paymentStatus === 'paid') {
        setStatus('success');
        setPaymentDetails(data);
      } else if (data.status === 'expired') {
        setStatus('expired');
      } else {
        // Still pending, poll again
        setAttempts(prev => prev + 1);
        setTimeout(pollPaymentStatus, 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      if (attempts < maxAttempts - 1) {
        setTimeout(pollPaymentStatus, 2000);
      } else {
        setStatus('error');
      }
    }
  };

  const formatCurrency = (cents) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(cents / 100);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }} data-testid="payment-success-page">
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '48px',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        {status === 'verifying' && (
          <>
            <Loader2 size={64} style={{ color: '#635bff', animation: 'spin 1s linear infinite', marginBottom: '24px' }} />
            <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>Verifying Payment</h1>
            <p style={{ color: '#6b7280' }}>Please wait while we confirm your payment...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div style={{
              width: '80px',
              height: '80px',
              background: '#dcfce7',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <CheckCircle size={48} style={{ color: '#16a34a' }} />
            </div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '12px', color: '#16a34a' }}>
              Payment Successful!
            </h1>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              Thank you for your payment. Your proposal has been marked as paid.
            </p>
            
            {paymentDetails && (
              <div style={{ 
                background: '#f9fafb', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '24px',
                textAlign: 'left'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ color: '#6b7280' }}>Amount Paid:</span>
                  <span style={{ fontWeight: '700', fontSize: '18px' }}>
                    {formatCurrency(paymentDetails.amountTotal)}
                  </span>
                </div>
                {paymentDetails.metadata?.proposalNumber && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#6b7280' }}>Proposal:</span>
                    <span style={{ fontWeight: '600' }}>{paymentDetails.metadata.proposalNumber}</span>
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <Button variant="outline" onClick={() => navigate('/')} data-testid="go-home-button">
                <Home size={18} /> Dashboard
              </Button>
              {paymentDetails?.metadata?.proposalId && (
                <Button 
                  onClick={() => navigate(`/proposal/${paymentDetails.metadata.proposalId}`)}
                  style={{ background: '#635bff' }}
                  data-testid="view-proposal-button"
                >
                  <FileText size={18} /> View Proposal
                </Button>
              )}
            </div>
          </>
        )}

        {status === 'timeout' && (
          <>
            <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px' }}>Payment Status Unknown</h1>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              We could not confirm your payment status. Please check your email for confirmation or contact support.
            </p>
            <Button onClick={() => navigate('/')} data-testid="go-home-button">
              <Home size={18} /> Go to Dashboard
            </Button>
          </>
        )}

        {(status === 'error' || status === 'expired') && (
          <>
            <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '12px', color: '#dc2626' }}>
              {status === 'expired' ? 'Session Expired' : 'Error'}
            </h1>
            <p style={{ color: '#6b7280', marginBottom: '24px' }}>
              {status === 'expired' 
                ? 'Your payment session has expired. Please try again.'
                : 'There was an error processing your payment. Please try again.'
              }
            </p>
            <Button onClick={() => navigate('/')} data-testid="go-home-button">
              <Home size={18} /> Go to Dashboard
            </Button>
          </>
        )}
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default PaymentSuccess;
